import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Grid, Stack, Typography, Card, IconButton, FormGroup, FormControlLabel, Checkbox, Select, MenuItem, Chip, CircularProgress, } from '@mui/material';
// icons
import { grey } from '@mui/material/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { formatTimestampToWithoutTime } from "utilities/common";
import { validateRegionKit } from "validations/hyperwolf/distributionmanagement";
import { addDistributionRegion, getUndistributedRegions } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from "../../../../redux/slices/common";
import { store } from "../../../../redux/store";
// custom components
import MDButton from 'components/MDButton';
import CommonLayout from "layouts/DashboardLayout";

export const showToastMsg = (msg, type, position, options = {}) => {
  const persist = Boolean(options.persist);
  if (type === "success") {
    store.dispatch(openSnackbar({
      content: msg != null ? String(msg) : "",
      color: "success",
      icon: "check",
      anchorOrigin: position,
      persist,
    }));
  } else {
    const errorMsg = msg?.response?.data?.message || msg?.message || msg;
    store.dispatch(openSnackbar({
      content: errorMsg != null ? String(errorMsg) : "",
      color: "error",
      icon: "warning",
      anchorOrigin: position,
      persist,
    }));
  }
}

const AddRegionKit = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { distributionId } = useParams();
  const [searchParams] = useSearchParams()
  const { distributionDate } = Object.fromEntries(searchParams.entries());

  const [details, setDetails] = useState({ regionId: "", subRegionIds: [], auto: true });
  const [errors, setErrors] = useState({});
  const [regionsList, setRegionsList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === "checkbox") {
      setDetails(prev => ({ ...prev, [name]: checked }));
    } else if (name === "regionId") {
      setDetails(prev => ({ ...prev, [name]: value, subRegionIds: [] }));
    } else {
      setDetails(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRegionsList = async () => {
    try {
      const response = await dispatch(getUndistributedRegions({ distributionId, limit: 100 })).unwrap()
      if (response?.data) {
        setRegionsList(response?.data || [])
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { myErrors, isValid } = validateRegionKit(details);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      const request = { distributionId, ...details }
      const response = await dispatch(addDistributionRegion(request)).unwrap();
      if (response?.data) {
        showToastMsg(response.message || "Success", "success");
        navigate(-1);
      }
    } catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    handleRegionsList();
  }, []);

  return (
    <CommonLayout>
      <div className='w-100'>
        <Box p={4} className="cus-region-kit">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
              <IconButton onClick={() => navigate(-1)}>
                <AngleRightIcon fontSize="14px" />
              </IconButton>
              <Typography variant="body2" fontWeight="semibold" ml={0.75}>
                Distribution ({formatTimestampToWithoutTime(Number(distributionDate))})
              </Typography>
            </Box>
          </Stack>

          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="semibold">Add Region Kit</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Region Name</Typography>
                <Select
                  size='large'
                  fullWidth
                  className='form-select'
                  name="regionId"
                  value={details?.regionId || ""}
                  onChange={handleChange}
                  displayEmpty
                  MenuProps={{
                    classes: {
                      root: "cus-dropdown-height",
                    }
                  }}
                  renderValue={selected => {
                    if (!selected) {
                      return <span style={{ color: '#aaa' }}>Region</span>;
                    }
                    const selectedRegion = regionsList?.find(region => region.regionId === selected);
                    return selectedRegion ? selectedRegion.region : '';
                  }}
                >
                  <MenuItem value="" disabled>Select Regions</MenuItem>
                  {
                    regionsList?.map((region) => (
                      <MenuItem key={region?.regionId} value={region?.regionId}>{region.region}</MenuItem>
                    ))
                  }
                </Select>
                {errors.regionId && <p className="error">{errors.regionId}</p>}
              </Grid>
              <Grid item xs={12} className="cus-multi-select">
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Sub Region Name</Typography>
                <Select
                  size='large'
                  fullWidth
                  className='form-select'
                  label=''
                  multiple={true}
                  name="subRegionIds"
                  value={details.subRegionIds || []}
                  onChange={handleChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} >
                      {selected.map((id) => {
                        const subRegion = regionsList?.find(region => region.regionId === details.regionId)?.subRegions?.find(d => d.blazeRegionId === id);
                        if (!subRegion) return null;

                        return (
                          <Chip
                            key={subRegion.blazeRegionId}
                            label={subRegion.name}
                            onDelete={(event) => {
                              event.stopPropagation();
                              // Remove the driverId from the array and update
                              const newSubRegions = details.subRegionIds?.filter(id => id !== subRegion.blazeRegionId);
                              // Simulate a synthetic event for handleSubRegionChange
                              handleChange({ target: { name: "subRegionIds", value: newSubRegions } })
                            }}
                            sx={{ m: 0.25 }}
                            // Prevent chip click from opening dropdown
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {
                    !details.regionId
                      ? <MenuItem value={""} disabled selected>
                        Select Region First
                      </MenuItem>
                      : regionsList?.find(region => region.regionId === details.regionId)?.subRegions?.map(subRegion => (
                        <MenuItem key={subRegion?.blazeRegionId} value={subRegion.blazeRegionId}>
                          {subRegion.name}
                        </MenuItem>
                      ))
                  }
                </Select>
                {errors.subRegionIds && <p className="error">{errors.subRegionIds}</p>}
              </Grid>
              <Grid item xs={12}>
                <FormGroup className='mb-1'>
                  <FormControlLabel
                    control={<Checkbox defaultChecked name="auto" checked={details.auto} onChange={handleChange} />}
                    label="Auto Split"
                  />
                </FormGroup>
                <Box style={{ borderRadius: "8px", background: "#F2F3F7", padding: "12px" }} className="d-flex gap-1">
                  <InfoOutlinedIcon className="cur-pointer" />
                  <span style={{ color: "#2E2E3A", fontSize: "14px" }}>
                    Automatically splits the pending inventory among drivers based on the parameters set in the distribution. Uncheck this option if you prefer to assign products manually.
                  </span>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    onClick={handleSave}
                  >
                    {isSubmitting ? <CircularProgress size={16} style={{ color: "#ffffff", fill: "#ffffff" }} /> : "Save"}

                  </MDButton>
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="outlined"
                    color="dark"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </MDButton>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Box>
      </div>
    </CommonLayout>
  )
}

export default AddRegionKit